import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { PhoneInput } from '../components/ui/phone-input';
import { Bus, User, Phone, Mail, CreditCard, ArrowRight, AlertCircle, ScanLine, Banknote, CheckCircle2, Printer, Search, Loader2, Calendar, Clock } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import PassportScanner from '../components/PassportScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BookingFormPage = () => {
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [scanIdx, setScanIdx] = useState(null);
  
  const seats = searchParams.get('seats')?.split(',') || [];
  const currency = searchParams.get('currency') || 'USD';
  
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [bookedFromLocation, setBookedFromLocation] = useState('Zimbabwe');
  const [paymentLocation, setPaymentLocation] = useState('Zimbabwe');
  const [isOnlineBooking] = useState(!window.location.pathname.includes('dashboard'));
  const staffProcessing = isStaff ? isStaff() : false;
  const [borderJumperFee, setBorderJumperFee] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [customerSearchIndex, setCustomerSearchIndex] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [passengers, setPassengers] = useState(
    seats.map(() => ({
      full_name: '',
      id_number: '',
      passport_number: '',
      phone: '',
      email: '',
      nationality: 'Zimbabwean',
      next_of_kin_name: '',
      next_of_kin_phone: '',
      sex: '',
      date_of_birth: '',
      age: '',
      education: '',
      occupation: '',
      is_local: false,
      is_child_under_5: false,
      is_complementary: false,
      is_border_jumper: false,
      boarding_point: '',
      dropoff_point: ''
    }))
  );
  const [payForward, setPayForward] = useState(false);
  const [payForwardAmount, setPayForwardAmount] = useState(0);
  const [payForwardCurrency, setPayForwardCurrency] = useState(currency || 'USD');
  const [isNegotiatedPrice, setIsNegotiatedPrice] = useState(false);
  const [negotiatedPricePerSeat, setNegotiatedPricePerSeat] = useState('');
  const [negotiationReason, setNegotiationReason] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);

  useEffect(() => {
    fetchTrip();
  }, [tripId]);

  useEffect(() => {
    if (!staffProcessing || customerSearchIndex === null || customerSearchTerm.trim().length < 3) {
      setCustomerSuggestions([]);
      return undefined;
    }

    let active = true;
    const timer = setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const response = await axios.get(`${API_URL}/customers/search`, {
          params: { q: customerSearchTerm.trim() }
        });
        if (active) setCustomerSuggestions(response.data || []);
      } catch (error) {
        if (active) setCustomerSuggestions([]);
      } finally {
        if (active) setSearchingCustomers(false);
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [customerSearchIndex, customerSearchTerm, staffProcessing]);

  const fetchTrip = async () => {
    try {
      const response = await axios.get(`${API_URL}/trips/${tripId}`);
      const tripData = response.data;
      setTrip(tripData);

      // Determine pickup and drop-off points from route
      let points = [];
      if (tripData.pickup_points && tripData.pickup_points.length > 0) {
        points = tripData.pickup_points;
      } else {
        const routeName = (tripData.route_name || '').toLowerCase();
        if (routeName.includes('masvingo') || routeName.includes('msv')) {
          points = ['Harare Roadport', 'Highglen', 'Chitungwiza', 'Beatrice', 'Furtherstone', 'Chivhu', 'Mvuma', 'Chaka', 'Masvingo', 'Chibi turnoff', 'Ngundu', 'Rutenga', 'Lundi', 'Bubi', 'Beitbridge Engen garage', 'Mooi River', 'Pietermaritzburg', 'Durban'];
        } else if (routeName.includes('bulawayo') || routeName.includes('byo')) {
          points = ['Harare Roadport', 'Whitehouse', 'Norton', 'Chegutu', 'Kadoma', 'Kwekwe', 'Gweru', 'Bulawayo', 'Esigodini', 'Gwanda', 'Colleen Bawn', 'West Nicholson', 'Makado', 'Beitbridge Engen garage', 'Mooi River', 'Pietermaritzburg', 'Durban'];
        } else {
          points = [tripData.origin, tripData.destination].filter(Boolean);
        }
      }
      setPickupPoints(points);

      // Default boarding/dropoff points for each passenger if available
      setPassengers(prev => prev.map(p => ({
        ...p,
        boarding_point: p.boarding_point || points[0] || tripData.origin || '',
        dropoff_point: p.dropoff_point || points[points.length - 1] || tripData.destination || ''
      })));
    } catch (error) {
      console.error('Failed to fetch trip:', error);
      toast.error('Failed to load trip details');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    try {
      const birthDate = new Date(dob);
      if (isNaN(birthDate.getTime())) return '';
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age >= 0 ? age : '';
    } catch (e) {
      return '';
    }
  };

  const updatePassenger = (index, field, value) => {
    const updated = [...passengers];
    updated[index][field] = value;

    // Auto calculate age and detect child under 5
    if (field === 'date_of_birth') {
      const calcAge = calculateAge(value);
      updated[index].age = calcAge;
      updated[index].is_child_under_5 = calcAge !== '' && calcAge < 5;
    }

    if (field === 'is_child_under_5' && value && !(updated[index].age !== '' && updated[index].age < 5)) {
      toast.error('Enter a date of birth confirming the passenger is under 5 to apply the free fare.');
      updated[index].is_child_under_5 = false;
    }

    if (field === 'is_local' && value === true) {
      updated[index].is_border_jumper = false;
      updated[index].passport_number = '';
    }

    if (field === 'is_border_jumper' && value === true) {
      updated[index].is_local = false;
      updated[index].passport_number = updated[index].passport_number || '';
    }

    // Complementary limit enforcement (max 3)
    if (field === 'is_complementary' && value === true) {
      const compCount = updated.filter(p => p.is_complementary).length;
      if (compCount > 3) {
        toast.error('Maximum 3 complementary tickets allowed per trip');
        updated[index].is_complementary = false;
        setPassengers(updated);
        return;
      }
    }

    setPassengers(updated);
  };

  const handleCustomerNameChange = (index, value) => {
    updatePassenger(index, 'full_name', value);
    if (!staffProcessing) {
      setCustomerSearchIndex(null);
      setCustomerSuggestions([]);
      return;
    }
    setCustomerSearchIndex(index);
    setCustomerSearchTerm(value);
  };

  const applyCustomerProfile = (index, customer) => {
    setPassengers(prev => prev.map((passenger, passengerIndex) => {
      if (passengerIndex !== index) return passenger;
      const nextPassenger = {
        ...passenger,
        full_name: customer.full_name || passenger.full_name,
        id_number: customer.id_number || passenger.id_number,
        passport_number: customer.passport_number || passenger.passport_number,
        phone: customer.phone || passenger.phone,
        email: customer.email || passenger.email,
        nationality: customer.nationality || passenger.nationality,
        next_of_kin_name: customer.next_of_kin_name || passenger.next_of_kin_name,
        next_of_kin_phone: customer.next_of_kin_phone || passenger.next_of_kin_phone,
        sex: customer.sex || passenger.sex,
        date_of_birth: customer.date_of_birth || passenger.date_of_birth,
        occupation: customer.occupation || passenger.occupation
      };
      if (nextPassenger.date_of_birth) {
        nextPassenger.age = calculateAge(nextPassenger.date_of_birth);
        nextPassenger.is_child_under_5 = nextPassenger.age !== '' && nextPassenger.age < 5;
      }
      return nextPassenger;
    }));
    setCustomerSuggestions([]);
    setCustomerSearchIndex(null);
    setCustomerSearchTerm('');
    toast.success('Customer details filled in.');
  };

  const validateForm = () => {
    let compCount = 0;
    const hasBorderJumper = passengers.some(p => p.is_border_jumper);
    if (hasBorderJumper && (!staffProcessing || !borderJumperFee || Number(borderJumperFee) <= 0)) {
      toast.error('A staff member must enter the border jumper fee before issuing this ticket');
      return false;
    }
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      if (!p.full_name || !p.phone) {
        toast.error(`Please fill in full name and phone for passenger ${i + 1}`);
        return false;
      }
      // Local passengers don't need ID or passport; cross-border passengers must provide passport
      if (!p.is_local && !p.passport_number) {
        toast.error(`Please provide Passport Number for cross-border passenger ${i + 1} (or check "Local Passenger")`);
        return false;
      }
      if (p.is_complementary) compCount++;
    }
    if (compCount > 3) {
      toast.error('Maximum 3 complementary staff tickets allowed per trip');
      return false;
    }
    if (!agreedTerms) {
      toast.error('Please agree to the terms and conditions');
      return false;
    }
    return true;
  };

  const standardPricePerSeat = trip ? (currency === 'USD' ? trip.price_usd : trip.price_zar) : 0;
  const isNegotiatedActive = isNegotiatedPrice && negotiatedPricePerSeat !== '' && !isNaN(Number(negotiatedPricePerSeat));
  const basePricePerSeat = isNegotiatedActive ? Number(negotiatedPricePerSeat) : standardPricePerSeat;
  const borderFee = Number(borderJumperFee) || 0;

  // Calculate total price dynamically considering children, complementary, and border jumpers
  const totalPrice = passengers.reduce((sum, p) => {
    if (p.is_child_under_5 || p.is_complementary) {
      return sum; // Free ticket
    }
    let passengerCost = basePricePerSeat;
    if (p.is_border_jumper) {
      passengerCost += borderFee;
    }
    return sum + passengerCost;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/bookings`, {
        trip_id: tripId,
        passengers: passengers,
        seat_numbers: seats,
        currency: currency,
        pay_forward_amount: payForward ? payForwardAmount : 0,
        pay_forward_currency: payForward ? payForwardCurrency : null,
        is_pay_forward: payForward,
        border_jumper_fee: borderFee,
        luggage_items: 0,
        pickup_point: passengers[0]?.boarding_point || trip?.origin,
        dropoff_point: passengers[0]?.dropoff_point || trip?.destination,
        booked_from_location: bookedFromLocation,
        payment_location: paymentLocation,
        is_online_booking: isOnlineBooking,
        negotiated_price: isNegotiatedActive ? Number(negotiatedPricePerSeat) : null,
        negotiation_reason: isNegotiatedActive ? (negotiationReason || 'Station counter negotiation') : null
      });

      if (!isOnlineBooking) {
        // Station booking — open the ticket so staff can choose the printer.
        toast.success('Station ticket confirmed! Choose a print option.');
        navigate(`/ticket/${response.data.booking_ref}`);
      } else {
        // Online booking — continue with payment page flow
        toast.success('Booking created successfully!');
        navigate(`/payment/${response.data.id}`);
      }
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const price = basePricePerSeat;
  const currencySymbol = currency === 'USD' ? '$' : 'R';



  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-40 bg-slate-200 rounded-lg" />
            <div className="h-96 bg-slate-200 rounded-lg" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!trip) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <AlertCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Trip Not Found</h2>
          <Button onClick={() => navigate('/')} className="btn-panashe">
            Search Again
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Trip Summary */}
        <Card className="card-panashe mb-6 overflow-hidden border-2 border-slate-200">
          {trip && (() => {
            const depDate = trip.departure_datetime ? parseISO(trip.departure_datetime) : null;
            const depToday = depDate ? isToday(depDate) : false;
            const depTomorrow = depDate ? isTomorrow(depDate) : false;

            return (
              <div className={`px-6 py-2.5 flex flex-wrap items-center justify-between gap-2 border-b ${
                depToday 
                  ? 'bg-emerald-600 text-white' 
                  : depTomorrow 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-800 text-white'
              }`}>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span className="text-xs uppercase font-bold tracking-wider opacity-90">Departure Date:</span>
                  <span className="font-extrabold text-sm sm:text-base underline decoration-white/40">
                    {depDate ? format(depDate, 'EEEE, dd MMMM yyyy') : ''}
                  </span>
                  {depToday && (
                    <span className="bg-white text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider">
                      ● TODAY'S TRIP
                    </span>
                  )}
                  {depTomorrow && (
                    <span className="bg-white text-blue-800 text-xs px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider">
                      ★ TOMORROW'S TRIP
                    </span>
                  )}
                </div>
                <div className="text-xs font-semibold opacity-90">
                  Bus: <span className="font-bold">{trip.bus_registration || 'Panashe Coach'}</span>
                </div>
              </div>
            );
          })()}

          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-panashe-blue rounded-xl flex items-center justify-center shadow">
                  <Bus className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
                    {trip.origin} → {trip.destination}
                  </h1>
                  <p className="text-sm text-slate-600 mt-0.5">{trip.route_name}</p>
                </div>
              </div>

              <div className="flex items-center gap-6 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
                <div className="text-center">
                  <div className="text-[11px] uppercase font-bold text-panashe-blue mb-0.5">Departs</div>
                  <p className="font-heading text-2xl font-extrabold text-slate-900 leading-none">
                    {trip.departure_datetime ? format(parseISO(trip.departure_datetime), 'HH:mm') : '--:--'}
                  </p>
                  <p className="text-xs font-bold text-panashe-blue mt-1">
                    {trip.departure_datetime ? format(parseISO(trip.departure_datetime), 'EEE, dd MMM yyyy') : ''}
                  </p>
                </div>

                <div className="text-slate-400 font-bold">➔</div>

                <div className="text-center">
                  <div className="text-[11px] uppercase font-bold text-emerald-700 mb-0.5">Arrives</div>
                  <p className="font-heading text-2xl font-extrabold text-slate-900 leading-none">
                    {trip.arrival_datetime ? format(parseISO(trip.arrival_datetime), 'HH:mm') : '--:--'}
                  </p>
                  <p className="text-xs font-bold text-emerald-700 mt-1">
                    {trip.arrival_datetime ? format(parseISO(trip.arrival_datetime), 'EEE, dd MMM yyyy') : ''}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-xs text-slate-500 font-medium">Selected Seats: <strong className="text-slate-800">{seats.join(', ')}</strong></p>
                <p className="font-heading text-3xl font-extrabold text-panashe-blue mt-0.5">
                  {currencySymbol}{totalPrice}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Passenger Forms */}
            <div className="lg:col-span-2 space-y-6">
              {staffProcessing && (
                <Card className="card-panashe bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-4">
                    <p className="font-semibold text-blue-900 text-sm mb-3">📍 Fare Payment Settlement Location</p>
                    <div>
                      <Label className="text-xs text-blue-800 font-medium">Paid From (Where is the fare being paid?) *</Label>
                      <Select value={paymentLocation} onValueChange={setPaymentLocation}>
                        <SelectTrigger className="bg-white mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Zimbabwe">🇿🇼 Zimbabwe Station / Office</SelectItem>
                          <SelectItem value="South Africa">🇿🇦 South Africa Station / Office</SelectItem>
                          <SelectItem value="Online Gateway">💳 Online Gateway (EcoCash / Card / Paystack)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-slate-500 mt-1">Station or gateway where this fare is paid and accounted for</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {passengers.map((passenger, idx) => (
                <Card key={idx} className="card-panashe" data-testid={`passenger-form-${idx}`}>
                  <CardHeader className="bg-slate-50 border-b">
                    <CardTitle className="font-heading text-lg flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        Passenger {idx + 1} - Seat {seats[idx]}
                      </span>
                      {isStaff && isStaff() && !passenger.is_local && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setScanIdx(idx)}
                          data-testid={`scan-passport-btn-${idx}`}
                        >
                          <ScanLine className="h-4 w-4 mr-1" /> Scan Passport
                        </Button>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    {/* Passenger Category Checkboxes */}
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Passenger Category &amp; Fare Options</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {staffProcessing && (
                          <>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`local-${idx}`}
                                checked={passenger.is_local}
                                onChange={(e) => updatePassenger(idx, 'is_local', e.target.checked)}
                                className="h-4 w-4 text-green-600 rounded"
                              />
                              <Label htmlFor={`local-${idx}`} className="cursor-pointer text-xs font-medium text-green-700">
                                🏠 Local Passenger (Exempt)
                              </Label>
                            </div>

                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`border-${idx}`}
                                checked={passenger.is_border_jumper}
                                onChange={(e) => updatePassenger(idx, 'is_border_jumper', e.target.checked)}
                                className="h-4 w-4 text-orange-600 rounded"
                              />
                              <Label htmlFor={`border-${idx}`} className="cursor-pointer text-xs font-medium text-orange-700">
                                🛂 Border Jumper
                              </Label>
                            </div>
                          </>
                        )}

                        {staffProcessing && passengers.some(p => p.is_border_jumper) && (
                          <div className="mt-3 md:col-span-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <Label htmlFor="border-jumper-fee" className="text-xs font-semibold text-orange-900">
                              Border Jumper Fee ({currencySymbol}) *
                            </Label>
                            <Input
                              id="border-jumper-fee"
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={borderJumperFee}
                              onChange={(e) => setBorderJumperFee(e.target.value)}
                              placeholder="Enter fee agreed by staff"
                              className="mt-1 bg-white"
                            />
                            <p className="text-[11px] text-orange-800 mt-1">This amount is set by staff and added to each selected border-jumper passenger.</p>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`child-${idx}`}
                            checked={passenger.is_child_under_5}
                            onChange={(e) => updatePassenger(idx, 'is_child_under_5', e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                          <Label htmlFor={`child-${idx}`} className="cursor-pointer text-xs font-medium text-blue-700">
                            👶 Child &lt; 5 (Free)
                          </Label>
                        </div>

                        {staffProcessing && !isOnlineBooking && (
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`comp-${idx}`}
                              checked={passenger.is_complementary}
                              onChange={(e) => updatePassenger(idx, 'is_complementary', e.target.checked)}
                              className="h-4 w-4 text-purple-600 rounded"
                            />
                            <Label htmlFor={`comp-${idx}`} className="cursor-pointer text-xs font-medium text-purple-700">
                              🎟️ Staff Comp (Max 3)
                            </Label>
                          </div>
                        )}

                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className={passenger.is_local ? 'md:col-span-2' : ''}>
                        <Label htmlFor={`name-${idx}`}>Full Name *</Label>
                        <div className="relative">
                          <Input
                            id={`name-${idx}`}
                            value={passenger.full_name}
                            onChange={(e) => handleCustomerNameChange(idx, e.target.value)}
                            placeholder={staffProcessing ? 'Type first 3 letters to find a returning customer' : 'Enter full name'}
                            required
                            data-testid={`passenger-name-${idx}`}
                          />
                          {staffProcessing && customerSearchIndex === idx && customerSearchTerm.trim().length >= 3 && (
                            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg overflow-hidden">
                              {searchingCustomers && (
                                <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin" /> Searching saved customers...
                                </div>
                              )}
                              {!searchingCustomers && customerSuggestions.length === 0 && (
                                <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                                  <Search className="h-4 w-4" /> No saved customer found
                                </div>
                              )}
                              {!searchingCustomers && customerSuggestions.map(customer => (
                                <button
                                  key={customer.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0"
                                  onClick={() => applyCustomerProfile(idx, customer)}
                                >
                                  <span className="block font-semibold text-slate-800">{customer.full_name}</span>
                                  <span className="block text-xs text-slate-500">
                                    {customer.phone || 'No phone'}{customer.passport_number ? ` • Passport ${customer.passport_number}` : ''}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Passport Number — only shown for Cross-Border passengers; hidden for Local */}
                      {!passenger.is_local && (
                        <div>
                          <Label htmlFor={`passport-${idx}`}>
                            Passport Number (Cross-Border) *
                          </Label>
                          <Input
                            id={`passport-${idx}`}
                            value={passenger.passport_number}
                            onChange={(e) => updatePassenger(idx, 'passport_number', e.target.value)}
                            placeholder="e.g., FN123456"
                            required={!passenger.is_local}
                            data-testid={`passenger-passport-${idx}`}
                          />
                        </div>
                      )}

                      {/* Sex and Date of Birth / Age */}
                      <div>
                        <Label htmlFor={`sex-${idx}`}>Sex *</Label>
                        <Select
                          value={passenger.sex}
                          onValueChange={(val) => updatePassenger(idx, 'sex', val)}
                        >
                          <SelectTrigger id={`sex-${idx}`}>
                            <SelectValue placeholder="Select sex" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <Label htmlFor={`dob-${idx}`}>Date of Birth *</Label>
                          {passenger.age !== '' && (
                            <span className="text-xs bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded">
                              Age: {passenger.age} yrs {passenger.age < 5 ? '(Under 5 - Free)' : ''}
                            </span>
                          )}
                        </div>
                        <Input
                          id={`dob-${idx}`}
                          type="date"
                          value={passenger.date_of_birth}
                          onChange={(e) => updatePassenger(idx, 'date_of_birth', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </div>

                      {/* Education & Occupation */}
                      <div>
                        <Label htmlFor={`edu-${idx}`}>Education Level</Label>
                        <Select
                          value={passenger.education}
                          onValueChange={(val) => updatePassenger(idx, 'education', val)}
                        >
                          <SelectTrigger id={`edu-${idx}`}>
                            <SelectValue placeholder="Select education" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Primary">Primary Education</SelectItem>
                            <SelectItem value="Secondary">Secondary (O-Level)</SelectItem>
                            <SelectItem value="Advanced Level">Advanced Level (A-Level)</SelectItem>
                            <SelectItem value="Diploma/Certificate">Diploma / Certificate</SelectItem>
                            <SelectItem value="Bachelor Degree">Bachelor's Degree</SelectItem>
                            <SelectItem value="Postgraduate">Postgraduate (Masters/PhD)</SelectItem>
                            <SelectItem value="None">None</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor={`occ-${idx}`}>Occupation</Label>
                        <Input
                          id={`occ-${idx}`}
                          value={passenger.occupation}
                          onChange={(e) => updatePassenger(idx, 'occupation', e.target.value)}
                          placeholder="e.g. Teacher, Trader, Engineer, Self-employed"
                        />
                      </div>

                      {/* Boarding and Drop-off Points along the Route */}
                      {pickupPoints.length > 0 && (
                        <>
                          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                            <Label htmlFor={`boarding-${idx}`} className="text-green-800 font-semibold text-xs flex items-center gap-1">
                              🚏 Specific Boarding Point
                            </Label>
                            <Select
                              value={passenger.boarding_point}
                              onValueChange={(val) => updatePassenger(idx, 'boarding_point', val)}
                            >
                              <SelectTrigger className="bg-white border-green-300 mt-1">
                                <SelectValue placeholder="Select boarding location" />
                              </SelectTrigger>
                              <SelectContent>
                                {pickupPoints.map((pt, i) => (
                                  <SelectItem key={i} value={pt}>{pt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                            <Label htmlFor={`dropoff-${idx}`} className="text-red-800 font-semibold text-xs flex items-center gap-1">
                              🏁 Specific Drop-off Point
                            </Label>
                            <Select
                              value={passenger.dropoff_point}
                              onValueChange={(val) => updatePassenger(idx, 'dropoff_point', val)}
                            >
                              <SelectTrigger className="bg-white border-red-300 mt-1">
                                <SelectValue placeholder="Select drop-off location" />
                              </SelectTrigger>
                              <SelectContent>
                                {pickupPoints.map((pt, i) => (
                                  <SelectItem key={i} value={pt}>{pt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}

                      <div>
                        <Label htmlFor={`phone-${idx}`}>Phone Number *</Label>
                        <PhoneInput
                          value={passenger.phone}
                          onChange={(val) => updatePassenger(idx, 'phone', val)}
                          placeholder="77 123 4567"
                          required
                          data-testid={`passenger-phone-${idx}`}
                        />
                      </div>

                      <div>
                        <Label htmlFor={`email-${idx}`}>Email (for ticket)</Label>
                        <Input
                          id={`email-${idx}`}
                          type="email"
                          value={passenger.email}
                          onChange={(e) => updatePassenger(idx, 'email', e.target.value)}
                          placeholder="email@example.com"
                          data-testid={`passenger-email-${idx}`}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <Label htmlFor={`nationality-${idx}`}>Nationality</Label>
                        <Select 
                          value={passenger.nationality}
                          onValueChange={(val) => updatePassenger(idx, 'nationality', val)}
                        >
                          <SelectTrigger data-testid={`passenger-nationality-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Zimbabwean">Zimbabwean</SelectItem>
                            <SelectItem value="South African">South African</SelectItem>
                            <SelectItem value="Mozambican">Mozambican</SelectItem>
                            <SelectItem value="Zambian">Zambian</SelectItem>
                            <SelectItem value="Botswanan">Botswanan</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Next of Kin Section */}
                      <div className="md:col-span-2 border-t pt-4 mt-2">
                        <p className="text-sm font-medium text-slate-700 mb-3">Emergency Contact / Next of Kin</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`nok-name-${idx}`}>Next of Kin Name</Label>
                            <Input
                              id={`nok-name-${idx}`}
                              value={passenger.next_of_kin_name}
                              onChange={(e) => updatePassenger(idx, 'next_of_kin_name', e.target.value)}
                              placeholder="Emergency contact name"
                              data-testid={`passenger-nok-name-${idx}`}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`nok-phone-${idx}`}>Next of Kin Phone</Label>
                            <PhoneInput
                              value={passenger.next_of_kin_phone}
                              onChange={(val) => updatePassenger(idx, 'next_of_kin_phone', val)}
                              placeholder="77 123 4567"
                              data-testid={`passenger-nok-phone-${idx}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pay Forward Option */}
              <Card className="card-panashe">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Payment Options
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <Checkbox 
                      id="payForward"
                      checked={payForward}
                      onCheckedChange={setPayForward}
                      data-testid="pay-forward-checkbox"
                    />
                    <div>
                      <Label htmlFor="payForward" className="cursor-pointer">
                        Pay Forward (Partial Payment)
                      </Label>
                      <p className="text-sm text-slate-500">
                        Pay a portion now and complete payment at the station before boarding
                      </p>
                    </div>
                  </div>

                  {payForward && (
                    <div className="ml-6 space-y-4">
                      <div>
                        <Label htmlFor="payForwardAmount">Amount to pay later ({currencySymbol})</Label>
                        <Input
                          id="payForwardAmount"
                          type="number"
                          min="0"
                          max={totalPrice - 1}
                          value={payForwardAmount}
                          onChange={(e) => setPayForwardAmount(Number(e.target.value))}
                          className="max-w-[200px]"
                          data-testid="pay-forward-amount"
                        />
                      </div>

                      <div>
                        <Label htmlFor="payForwardCurrency">Pay Forward Currency at Destination/Station</Label>
                        <Select
                          value={payForwardCurrency}
                          onValueChange={setPayForwardCurrency}
                        >
                          <SelectTrigger className="max-w-[250px]" data-testid="pay-forward-currency-select">
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD ($ - US Dollars)</SelectItem>
                            <SelectItem value="ZAR">ZAR (R - South African Rands)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        <p className="font-semibold">💰 Pay Forward Summary:</p>
                        <p>Pay now: <strong>{currencySymbol}{(totalPrice - payForwardAmount).toFixed(2)}</strong></p>
                        <p>
                          Pay later at station: <strong>
                            {payForwardCurrency === 'ZAR' && currency === 'USD'
                              ? `R${(payForwardAmount * 18.5).toFixed(2)} ZAR ($${payForwardAmount.toFixed(2)} USD equivalent)`
                              : payForwardCurrency === 'USD' && currency === 'ZAR'
                              ? `$${(payForwardAmount / 18.5).toFixed(2)} USD (R${payForwardAmount.toFixed(2)} ZAR equivalent)`
                              : `${currencySymbol}${payForwardAmount.toFixed(2)} ${currency}`
                            }
                          </strong>
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {staffProcessing && (
                <Card className="card-panashe border-indigo-200 bg-indigo-50/40">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-indigo-900 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        🤝 Price Negotiation (Station Counter)
                      </span>
                      <span className="text-xs bg-indigo-100 text-indigo-800 font-semibold px-2 py-0.5 rounded border border-indigo-300">
                        Station Only
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="negotiatedPriceCheckbox"
                        checked={isNegotiatedPrice}
                        onCheckedChange={setIsNegotiatedPrice}
                        data-testid="negotiated-price-checkbox"
                      />
                      <div>
                        <Label htmlFor="negotiatedPriceCheckbox" className="font-medium cursor-pointer text-indigo-950">
                          Apply Negotiated Ticket Fare
                        </Label>
                        <p className="text-xs text-slate-500">
                          Available when processing tickets at station counters. Overrides standard trip tariff.
                        </p>
                      </div>
                    </div>

                    {isNegotiatedPrice && (
                      <div className="space-y-3 pt-2 border-t border-indigo-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="negotiatedFare" className="text-xs font-semibold text-indigo-900">
                              Agreed Price per Seat ({currencySymbol}) *
                            </Label>
                            <Input
                              id="negotiatedFare"
                              type="number"
                              min="0"
                              step="1"
                              value={negotiatedPricePerSeat}
                              onChange={(e) => setNegotiatedPricePerSeat(e.target.value)}
                              placeholder={`Standard: ${currencySymbol}${standardPricePerSeat}`}
                              className="bg-white border-indigo-300 font-semibold text-indigo-900"
                              data-testid="negotiated-price-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="negotiationReason" className="text-xs font-semibold text-indigo-900">
                              Negotiation Reason / Authorization
                            </Label>
                            <Input
                              id="negotiationReason"
                              value={negotiationReason}
                              onChange={(e) => setNegotiationReason(e.target.value)}
                              placeholder="e.g. Elderly discount, Group deal, Approved by Manager"
                              className="bg-white border-indigo-300"
                              data-testid="negotiation-reason-input"
                            />
                          </div>
                        </div>

                        {isNegotiatedActive && (
                          <div className="p-2.5 bg-indigo-100 border border-indigo-200 rounded text-xs text-indigo-950 flex items-center justify-between">
                            <span>
                              Standard: <s className="text-slate-500">{currencySymbol}{standardPricePerSeat}</s> → Agreed: <strong className="text-indigo-900">{currencySymbol}{Number(negotiatedPricePerSeat).toFixed(2)}</strong> / seat
                            </span>
                            <span className="font-bold text-indigo-700 bg-white px-2 py-0.5 rounded border border-indigo-200">
                              {standardPricePerSeat > Number(negotiatedPricePerSeat) 
                                ? `Saving: ${currencySymbol}${(standardPricePerSeat - Number(negotiatedPricePerSeat)).toFixed(2)}` 
                                : 'Custom Agreed Rate'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Terms */}
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="terms"
                  checked={agreedTerms}
                  onCheckedChange={setAgreedTerms}
                  data-testid="terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer">
                  I agree to the terms and conditions, cancellation policy, and confirm that all passenger 
                  information provided is accurate. I understand that valid ID/Passport is required for boarding.
                </Label>
              </div>
            </div>

            {/* Summary */}
            <div>
              <Card className="card-panashe sticky top-24">
                <CardHeader className="bg-panashe-blue text-white rounded-t-lg">
                  <CardTitle className="font-heading">Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Route</span>
                      <span className="font-medium">{trip.origin} → {trip.destination}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Date</span>
                      <span className="font-medium">{format(parseISO(trip.departure_datetime), 'dd MMM yyyy')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Time</span>
                      <span className="font-medium">{format(parseISO(trip.departure_datetime), 'HH:mm')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Seats</span>
                      <span className="font-medium">{seats.join(', ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Passengers</span>
                      <span className="font-medium">{seats.length}</span>
                    </div>

                    <div className="border-t border-slate-200 pt-3 mt-3">
                      <div className="flex justify-between mb-2">
                        <span className="text-slate-600">{seats.length} × {currencySymbol}{price}</span>
                        <span>{currencySymbol}{totalPrice.toFixed(2)}</span>
                      </div>
                      {isNegotiatedActive && (
                        <div className="text-xs text-indigo-700 bg-indigo-50 p-1.5 rounded border border-indigo-200 mb-2 flex justify-between items-center">
                          <span>🤝 Station Negotiated Fare</span>
                          <span className="font-semibold"><s>{currencySymbol}{standardPricePerSeat}</s> → {currencySymbol}{Number(negotiatedPricePerSeat).toFixed(2)}</span>
                        </div>
                      )}
                      
                      {payForward && payForwardAmount > 0 ? (
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-slate-600 text-sm">
                            <span>Total Fare</span>
                            <span className="font-medium">{currencySymbol}{totalPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-green-700 text-sm">
                            <span>✅ Pay Now (at booking)</span>
                            <span className="font-bold">{currencySymbol}{(totalPrice - payForwardAmount).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-orange-700 text-sm border-t pt-1.5">
                            <span>💰 Balance at Station</span>
                            <span className="font-bold">
                              {payForwardCurrency === 'ZAR' && currency === 'USD'
                                ? `R${(payForwardAmount * 18.5).toFixed(2)} ZAR`
                                : payForwardCurrency === 'USD' && currency === 'ZAR'
                                ? `$${(payForwardAmount / 18.5).toFixed(2)} USD`
                                : `${currencySymbol}${payForwardAmount.toFixed(2)}`
                              }
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between font-bold text-lg text-panashe-blue">
                          <span>Total</span>
                          <span>{currencySymbol}{totalPrice.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button 
                    type="submit"
                    className="w-full btn-panashe mt-6"
                    disabled={submitting || !agreedTerms}
                    data-testid="proceed-payment-btn"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner" />
                        Processing...
                      </span>
                    ) : isOnlineBooking ? (
                      <>
                        Proceed to Payment
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Banknote className="h-4 w-4" />
                        Create Booking &amp; Collect Cash
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>

      <PassportScanner
        open={scanIdx !== null}
        onClose={() => setScanIdx(null)}
        onResult={(r) => {
          if (scanIdx === null) return;
          const updated = [...passengers];
          const target = updated[scanIdx];
          const fullName = r.full_name || [r.given_names, r.surname].filter(Boolean).join(' ');
          if (fullName) target.full_name = fullName;
          const passNo = r.passport_number || r.document_number;
          if (passNo) target.passport_number = passNo;
          if (r.nationality) {
            const mapped = { ZWE: 'Zimbabwean', ZAF: 'South African', MOZ: 'Mozambican',
                             ZMB: 'Zambian', BWA: 'Botswanan' }[r.nationality] || r.nationality;
            target.nationality = mapped;
          }
          setPassengers(updated);
        }}
      />
    </PublicLayout>
  );
};

export default BookingFormPage;
